/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-restricted-syntax */
/* eslint-disable react/jsx-no-bind */
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  FormikProvider, useFormik, Form, FieldArray, ArrayHelpers, FormikValues, Formik,
} from 'formik';
import {
  Layout, Spin, Row, Col, Popconfirm, message,
} from 'antd';

import { Input, Table, Form as AntForm } from 'formik-antd';
import classNames from 'classnames';
import clone from 'lodash.clone';
import { useStore } from '../../../store';
import { assetTypeValidationRules } from '../../../store/modules/asset/asset-type.validation';
import PageTitle from '../../lib/page-title';
import PrimaryButton from '../../lib/primary-button';
import { IFormFields } from '../../lib/field-edit-form/field-edit-form';
import DeleteModal from '../../lib/delete-modal';
import SecondaryButton from '../../lib/secondary-button';
import NewProperty from '../../lib/new-property';
import LeavePageModal from '../../lib/leave-page-modal';
import { useActions } from '../../providers/actions-provider/actions.context';

import './asset-details.less';
import checkPermission from '../../../utils/permissions/permissions';
import { ReactComponent as ThrashCan } from '../../../assets/icons/thrashCan.svg';
import { AssetIcon } from '../../lib/asset-icon/AssetIcon';
import { useAssetIcon } from '../../hooks/useAssetIcon';
import { validateIcon } from '../../../utils/asset-icon/validate-icon';

// eslint-disable-next-line
const buildColumns = (write: boolean, formik: any, arrayHelpers: ArrayHelpers, propertiesStatus: any) => [{
  title: 'Name',
  dataIndex: 'name',
  // eslint-disable-next-line
  render: (text: string, record: any, i: number) => (write ? (
    <AntForm.Item name={`properties.${i}.name`}><Input name={`properties.${i}.name`} /></AntForm.Item>
  ) : text),
}, {
  title: 'Value',
  dataIndex: 'value',
  // eslint-disable-next-line
  render: (text: string, record: any, i: number) => (write ? (
    <AntForm.Item name={`properties.${i}.value`}><Input name={`properties.${i}.value`} /></AntForm.Item>
  ) : text),
}, {
  title: 'Delete',
  dataIndex: 'delete',
  // eslint-disable-next-line
  render: (text: string, record: any, i: number) => (
    <Popconfirm
      title="Are you sure you want to delete this record?"
      onConfirm={() => {
        const status = propertiesStatus.current[record.id];
        if (status) {
          status.removed = true;
        }
        arrayHelpers.remove(i);
      }}
    >
      <button type="button" disabled={!write}>
        <ThrashCan />
      </button>
    </Popconfirm>
  ),
}].map((c) => ({ ...c, key: c.dataIndex }));

const AssetDetails = (): JSX.Element => {
  // tools for working
  const { assetActions } = useActions();
  const { assetType, currentUser, auth } = useStore();
  const { id: currentAssetID } = useParams<{ id: string }>();
  // state for creation of new asset
  const currentTenant = currentUser.data.tenant || auth.data.tenant;
  // leave page mechanism
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const propertiesStatus = useRef({});

  const hasNotWritePermission = !checkPermission('assetTypes', 'write');

  const isExistingAssetType = currentAssetID !== 'new';
  const assetTypeCode = assetType.data?.code;
  const { url: iconUrl } = useAssetIcon(isExistingAssetType ? assetTypeCode : undefined);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [iconRemoved, setIconRemoved] = useState<boolean>(false);
  const [iconUploading, setIconUploading] = useState<boolean>(false);
  const iconInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setIconPreview(null);
    setIconRemoved(false);
  }, [assetTypeCode]);

  const displayIcon = iconRemoved ? null : (iconPreview ?? iconUrl);

  const resetIconInput = (): void => {
    if (iconInputRef.current) {
      iconInputRef.current.value = '';
    }
  };

  const handleIconChange = async (file: File | null | undefined): Promise<void> => {
    if (!file || !assetTypeCode) return;
    const result = validateIcon(file);
    if (!result.ok) {
      message.error(result.reason);
      resetIconInput();
      return;
    }
    setIconPreview(URL.createObjectURL(file));
    setIconRemoved(false);
    setIconUploading(true);
    try {
      await assetActions.uploadAssetIcon(assetTypeCode, file);
      message.success('Icon updated');
    } catch {
      message.error('Failed to upload icon');
    } finally {
      setIconUploading(false);
      resetIconInput();
    }
  };

  const handleIconRemove = async (): Promise<void> => {
    if (!assetTypeCode) return;
    setIconUploading(true);
    try {
      await assetActions.deleteAssetIcon(assetTypeCode);
      setIconRemoved(true);
      setIconPreview(null);
      message.success('Icon removed');
    } catch {
      message.error('Failed to remove icon');
    } finally {
      setIconUploading(false);
    }
  };

  // get asset type
  useEffect(() => {
    if (currentAssetID !== 'new') {
      assetActions.getAssetType(+currentAssetID);
    }
  }, [currentAssetID]);

  // create or edit some asset - preparing initialValues
  useEffect(() => {
    if (assetType.loaded) {
      propertiesStatus.current = assetType.data.properties.reduce(
        (statuses, property) => ({
          ...statuses, [property.id]: { removed: false, snapshot: clone(property) },
        }),
        {},
      );
    }
  }, [currentAssetID, assetType]);

  // removing fc
  // eslint-disable-next-line
  const onDelete = (id: number, properties: any[]) => {
    const propertiesId: Array<number> = [];
    properties.forEach((property) => {
      if (property.id) {
        propertiesId.push(property.id);
      }
    });
    assetActions.deleteAssetType(propertiesId, id);
  };

  const formik = useFormik({
    initialValues: assetType.data,
    validationSchema: assetTypeValidationRules,
    onSubmit: async (values) => {
      if (currentAssetID === 'new') {
        assetActions.addAssetType({
          tenant: currentTenant,
          code: values.code,
          description: values.description,
          properties: values.properties,
        });
      } else {
        await assetActions.updateAssetType(values, propertiesStatus.current);
        propertiesStatus.current = {};
      }
    },
    enableReinitialize: true,
  });
  const { isSubmitting } = formik;

  const toggleModal = () => {
    setModalVisible(!modalVisible);
  };

  const onConfirmDelete = () => {
    onDelete(Number(currentAssetID), formik.values.properties);
    toggleModal();
  };

  return (
    <Layout className="main-container">
      {formik.dirty && !isSubmitting && <LeavePageModal /> }

      {modalVisible && (
        <DeleteModal
          text={formik.values.code}
          modalVisible={modalVisible}
          onConfirm={onConfirmDelete}
          onCancel={toggleModal}
        />
      )}
      <PageTitle title="Asset Details" back>
        <div>
          {!hasNotWritePermission && currentAssetID !== 'new' && (
            <SecondaryButton
              className="left"
              onClick={toggleModal}
            >
              Delete Asset Type
            </SecondaryButton>
          )}

        </div>
      </PageTitle>
      <FormikProvider
        value={formik}
      >
        {!assetType.loading || currentAssetID === 'new' ? (
          <AntForm className="asset-form" style={{ width: '100%' }}>
            {isExistingAssetType && (
              <Row style={{ marginBottom: 16 }}>
                <Col>
                  <span className="ant-form-item-label">Icon</span>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 16, marginTop: 4,
                  }}
                  >
                    <AssetIcon src={displayIcon} size={72} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label
                        htmlFor="asset-type-icon-upload"
                        style={{
                          color: '#3800EE',
                          fontSize: 13,
                          cursor: iconUploading ? 'default' : 'pointer',
                          opacity: iconUploading ? 0.6 : 1,
                        }}
                      >
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {iconUploading ? 'Uploading…' : (displayIcon ? 'Replace icon' : 'Add icon')}
                      </label>
                      <input
                        id="asset-type-icon-upload"
                        ref={iconInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        disabled={iconUploading}
                        onChange={(event) => { handleIconChange(event.target.files?.[0]); }}
                        style={{ display: 'none' }}
                      />
                      {displayIcon && !iconUploading && (
                        <button
                          type="button"
                          onClick={() => { handleIconRemove(); }}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            textAlign: 'left',
                            color: '#B0B0B0',
                            fontSize: 12,
                            cursor: 'pointer',
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            )}
            <Row gutter={[10, 0]}>
              <Col>
                <AntForm.Item label="Code" name="code">
                  {hasNotWritePermission ? (
                    <span className="asset-form__readonly">{formik.values.code}</span>
                  ) : (
                    <Input name="code" className={classNames('form-control__input', 'asset-form__input')} />
                  )}
                </AntForm.Item>
              </Col>
              <Col>
                <AntForm.Item label="Description" name="description">
                  {hasNotWritePermission ? (
                    <span className="asset-form__readonly">{formik.values.description}</span>
                  ) : (
                    <Input name="description" className={classNames('form-control__input', 'asset-form__input')} />
                  )}
                </AntForm.Item>
              </Col>
            </Row>
            <span className="ant-form-item-label" style={{ padding: '10 0' }}>Properties</span>
            <Row>
              <Col span={24}>
                <FieldArray name="properties">
                  {(arrayHelpers: ArrayHelpers) => (
                    <>
                      <Table
                        className="properties-table"
                        name="properties"
                        columns={buildColumns(
                          !hasNotWritePermission,
                          formik,
                          arrayHelpers,
                          propertiesStatus,
                        )}
                        rowKey="id"
                        pagination={false}
                      />
                      {hasNotWritePermission ? '' : (
                        <NewProperty
                          disabled={false}
                          onClick={() => {
                            const { properties } = formik.values;
                            let id = 0;
                            if (properties && properties.length > 0) {
                              id = properties[0].id;
                              properties.forEach((prop) => {
                                if (prop.id > id) {
                                  id = prop.id;
                                }
                              });
                              id += 1;
                            }
                            arrayHelpers.push({ id });
                          }}
                          title="New Property"
                        />
                      )}
                    </>
                  )}
                </FieldArray>
              </Col>
            </Row>
            { !hasNotWritePermission && (
              <PrimaryButton
                className="asset-form__submit"
                title="Save"
                type="submit"
                disabled={!formik.dirty}
              />
            )}
          </AntForm>
        ) : <Spin size="large" />}
      </FormikProvider>
    </Layout>
  );
};

AssetDetails.displayName = 'AssetDetails';

export default AssetDetails;
